/**
 * Sales-channel setup store (TV1-1904).
 *
 * Zustand-based CDSE store for the per-channel setup wizard (inventory /
 * billing entity / logistics). Registered ONCE at the composition root — the
 * channels container gate (see `channels/components/Channels.tsx`).
 */

import { useCallback } from 'react';
import { createStore } from '@commandDrivenStateEngine/core/createStore';
import type { CommandMetaInput, Dispatch } from '@commandDrivenStateEngine/core/types';
import { ClientLibraryType } from '@bett3r-dev/teselly-client-library';
import useAuth from '@contexts/useAuth/useAuth';
import { ConnectorEntry } from '../../shared/state/state';
import { initialState } from './state';
import { handlers } from './handlers';
import { invariants } from './invariants';
import { SalesChannelSetupCommand } from './commands';
import { createSetupInitPolicy } from './policies/setupInitPolicy';
import { createSetupSubmitPolicy } from './policies/setupSubmitPolicy';

const {
  useSelector,
  useErrors,
  usePolicies,
  getState,
  dispatch
} = createStore({
  name: 'salesChannelSetup',
  initialState,
  handlers,
  invariants
});

/**
 * Dispatch hook that attaches the authenticated user to user-sourced commands
 * so the `canApply` capability gates can authorize them (mirrors
 * `useOnboardingDispatch`).
 */
const useDispatch = (): Dispatch<SalesChannelSetupCommand> => {
  const auth = useAuth();
  const user = auth?.user ?? null;
  return useCallback<Dispatch<SalesChannelSetupCommand>>(
    ( command, metaInput: CommandMetaInput ) =>
      dispatch(
        command,
        metaInput.source === 'user' && metaInput.user === undefined
          ? { ...metaInput, user }
          : metaInput
      ),
    [user]
  );
};

/**
 * Resolves the integration id of the channel whose detail is currently open.
 * Supplied by the composition root (the channels gate) from the channels store,
 * so the init policy knows which channel to configure without coupling this
 * store to the channels store.
 */
export type GetActiveChannelId = () => string | null;

/**
 * Resolves the connector catalog as a `Record<connectorId, ConnectorEntry>`.
 * Supplied by the composition root (the gate) from the integrations/shared
 * store, so the submit policy can branch via `resolveSetupPanelKind` when
 * provisioning an invoicing / logistics integration inline — without coupling
 * this store to the shared store.
 */
export type GetConnectors = () => Record<string, ConnectorEntry>;

/**
 * Policy factory. Creates all setup policies with their runtime dependencies.
 */
export const createSalesChannelSetupPolicies = (
  backend: ClientLibraryType,
  getActiveChannelId: GetActiveChannelId,
  getConnectors: GetConnectors
) => [
  createSetupInitPolicy( backend, getActiveChannelId ),
  createSetupSubmitPolicy( backend, getConnectors )
];

export {
  useSelector as useSalesChannelSetupSelector,
  useErrors as useSalesChannelSetupErrors,
  useDispatch as useSalesChannelSetupDispatch,
  usePolicies as useSalesChannelSetupPolicies,
  getState as getSalesChannelSetupState,
  dispatch as dispatchSalesChannelSetupCommand
};

export { selectors } from './state';
